import { useParams, Link } from "react-router-dom";
import { Phone, CheckCircle2, ArrowRight } from "lucide-react";
import SeoHead, { localBusinessJsonLd, breadcrumbJsonLd } from "@/components/SeoHead";
import QuoteForm from "@/components/QuoteForm";
import CitiesGrid from "@/components/CitiesGrid";
import CtaBanner from "@/components/CtaBanner";
import NotFound from "@/pages/NotFound";
import { findServiceBySlug, SERVICES } from "@/data/services";
import { SITE, HERO_IMAGES } from "@/data/siteData";

export default function ServicePage() {
  const { slug } = useParams();
  const service = findServiceBySlug(slug);
  if (!service) return <NotFound />;

  const title = `${service.title} à Melun - ${SITE.name}`;
  const desc = `${service.title} pour restaurants et cuisines professionnelles à Melun et en Seine-et-Marne. ${service.short} Devis gratuit. ☎ ${SITE.phoneDisplay}.`;

  const jsonLd = [
    localBusinessJsonLd({
      "@type": "Service",
      serviceType: service.title,
      name: service.title,
      description: service.intro,
    }),
    breadcrumbJsonLd([
      { name: "Accueil", path: "/" },
      { name: "Services", path: "/services" },
      { name: service.title, path: `/services/${service.slug}` },
    ]),
  ];

  const otherServices = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 6);

  return (
    <>
      <SeoHead title={title} description={desc} canonicalPath={`/services/${service.slug}`} jsonLd={jsonLd} image={HERO_IMAGES.beforeAfter} />

      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 dh-radial-glow pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-16 md:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <nav className="flex items-center gap-2 text-sm text-slate-500 mb-5">
              <Link to="/" className="hover:text-[#00367e]">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-[#00367e]">Services</Link>
              <span>/</span>
              <span className="text-[#00367e] font-semibold">{service.title}</span>
            </nav>
            <div className="inline-flex items-center gap-2 mb-5 px-4 py-2 rounded-full bg-[#f1f5fb] text-[#00367e] text-sm font-semibold uppercase tracking-wider">
              Service expert
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#00367e]">
              {service.title}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-700 leading-relaxed">{service.intro}</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${SITE.phoneTel}`}
                data-testid={`service-call-${service.slug}`}
                className="dh-cta-shine inline-flex items-center justify-center gap-2 px-7 py-5 rounded-full bg-[#00367e] hover:bg-[#066ec4] text-white font-extrabold text-lg shadow-xl transition-all"
              >
                <Phone size={20} /> APPELER {SITE.phoneDisplay}
              </a>
              <a
                href="#devis"
                className="inline-flex items-center justify-center gap-2 px-7 py-5 rounded-full bg-white border-2 border-[#00367e] text-[#00367e] hover:bg-[#00367e] hover:text-white font-extrabold text-lg transition-all"
              >
                Devis gratuit
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <img
              src={HERO_IMAGES.beforeAfter}
              alt={service.title}
              width="900" height="900"
              loading="eager"
              className="w-full rounded-3xl shadow-[0_30px_60px_rgba(0,54,126,0.18)] object-cover aspect-square"
            />
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#00367e] leading-[1.1] mb-8">
            Ce que comprend la prestation
          </h2>
          <ul className="space-y-4">
            {service.details.map((d, i) => (
              <li
                key={i}
                className="flex items-start gap-4 bg-[#f8fafc] rounded-2xl p-5 border border-slate-100"
                data-testid={`service-detail-${service.slug}-${i}`}
              >
                <span className="w-8 h-8 rounded-full bg-[#00367e] text-white flex items-center justify-center shrink-0">
                  <CheckCircle2 size={18} />
                </span>
                <span className="text-slate-800 text-[16px] leading-relaxed pt-1">{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CtaBanner
        title={`${service.title} : devis gratuit à Melun`}
        subtitle="Intervention sous 24h en Seine-et-Marne. Attestation officielle remise après chaque intervention."
      />

      {/* Other services */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-[#00367e] mb-8">Nos autres services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {otherServices.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group flex items-center justify-between bg-[#f8fafc] rounded-2xl p-5 border border-slate-100 hover:border-[#066ec4] transition-all"
              >
                <span className="font-bold text-[#00367e]">{s.title}</span>
                <ArrowRight size={18} className="text-slate-400 group-hover:text-[#066ec4] group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CitiesGrid title={<>Ce service est disponible dans toute la <span className="dh-underline">Seine-et-Marne</span></>} />
      <QuoteForm preselectedService={service.title} />
    </>
  );
}
